import { supabase } from '../lib/supabase';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export const printerService = {
  // ============================================================================
  // PRINTER STATUS & MONITORING
  // ============================================================================

  async getPrinterStatus() {
    // Simulated printer status - in production, this would connect to actual Evolis Badgy API
    return {
      connected: true,
      model: 'Evolis Badgy200',
      status: 'ready',
      ribbonLevel: 85,
      cardStock: 120,
      lastMaintenance: '2026-01-10',
      nextMaintenance: '2026-02-10',
      errorCount: 0,
      totalPrinted: 1247
    };
  },

  async getPrintQueue() {
    const { data, error } = await supabase
      ?.from('id_card_generation_logs')
      ?.select(`
        *,
        card:id_cards(
          *,
          employee:employees(*)
        )
      `)
      ?.eq('action_type', 'printed')
      ?.order('performed_at', { ascending: false })
      ?.limit(50);

    if (error) throw error;

    return data?.map(row => ({
      id: row?.id,
      cardId: row?.card_id,
      employeeName: `${row?.card?.employee?.first_name} ${row?.card?.employee?.last_name}`,
      department: row?.card?.employee?.department,
      status: row?.print_status || 'completed',
      printedAt: row?.performed_at,
      printerModel: row?.printer_model,
      batchId: row?.batch_id
    })) || [];
  },

  async getProductionStatistics(startDate, endDate) {
    const { data, error } = await supabase
      ?.from('id_card_generation_logs')
      ?.select('*')
      ?.eq('action_type', 'printed')
      ?.gte('performed_at', startDate)
      ?.lte('performed_at', endDate);

    if (error) throw error;

    const total = data?.length || 0;
    const successful = data?.filter(log => log?.print_status !== 'failed')?.length || 0;
    const failed = total - successful;

    return {
      totalPrinted: total,
      successfulPrints: successful,
      failedPrints: failed,
      errorRate: total > 0 ? ((failed / total) * 100)?.toFixed(2) : 0,
      averagePerDay: total > 0 ? (total / 7)?.toFixed(1) : 0
    };
  },

  // ============================================================================
  // EXPORT FUNCTIONALITY
  // ============================================================================

  async exportCardToPDF(cardElement, employeeName) {
    try {
      const canvas = await html2canvas(cardElement, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas?.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: [85.6, 53.98] // Standard ID card size
      });

      pdf?.addImage(imgData, 'PNG', 0, 0, 85.6, 53.98);
      pdf?.save(`${employeeName}_ID_Card.pdf`);

      return { success: true };
    } catch (error) {
      console.error('PDF export error:', error);
      throw new Error('Failed to export card to PDF');
    }
  },

  async exportCardToImage(cardElement, employeeName, format = 'png') {
    try {
      const canvas = await html2canvas(cardElement, {
        scale: 3,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const link = document.createElement('a');
      link.download = `${employeeName}_ID_Card.${format}`;
      link.href = canvas?.toDataURL(`image/${format}`);
      link?.click();

      return { success: true };
    } catch (error) {
      console.error('Image export error:', error);
      throw new Error(`Failed to export card to ${format.toUpperCase()}`);
    }
  },

  // ============================================================================
  // BATCH PRINTING
  // ============================================================================

  async batchPrint(cardIds, printerModel = 'Evolis Badgy200') {
    const batchId = crypto.randomUUID();
    const logs = [];

    for (const cardId of cardIds) {
      const log = {
        cardId,
        actionType: 'printed',
        performedBy: 'system',
        printerModel,
        batchId,
        printStatus: 'completed',
        metadata: { batchPrint: true }
      };

      const { data, error } = await supabase
        ?.from('id_card_generation_logs')
        ?.insert({
          card_id: log?.cardId,
          action_type: log?.actionType,
          performed_by: log?.performedBy,
          printer_model: log?.printerModel,
          batch_id: log?.batchId,
          print_status: log?.printStatus,
          metadata: log?.metadata
        })
        ?.select()
        ?.single();

      if (!error) {
        logs?.push(data);
      }
    }

    return {
      batchId,
      totalCards: cardIds?.length,
      successfulPrints: logs?.length,
      failedPrints: cardIds?.length - logs?.length
    };
  },

  // ============================================================================
  // PRINTER MAINTENANCE
  // ============================================================================

  async scheduleMaintenance(maintenanceDate, notes) {
    // In production, this would integrate with printer management system
    return {
      success: true,
      scheduledDate: maintenanceDate,
      notes
    };
  },

  async getMaintenanceHistory() {
    // Simulated maintenance history
    return [
      {
        id: '1',
        date: '2026-01-10',
        type: 'Routine Cleaning',
        performedBy: 'Admin',
        notes: 'Cleaned print head and replaced ribbon'
      },
      {
        id: '2',
        date: '2025-12-15',
        type: 'Ribbon Replacement',
        performedBy: 'Admin',
        notes: 'Replaced YMCKO ribbon'
      }
    ];
  },

  // ============================================================================
  // SUPPLY MANAGEMENT
  // ============================================================================

  async getSupplyLevels() {
    return {
      ribbon: {
        level: 85,
        type: 'YMCKO',
        estimatedPrints: 170,
        reorderThreshold: 20
      },
      cardStock: {
        level: 120,
        type: 'PVC Cards',
        reorderThreshold: 50
      },
      cleaningKit: {
        level: 3,
        lastUsed: '2026-01-10',
        reorderThreshold: 1
      }
    };
  },

  async updateSupplyLevels(supplies) {
    // In production, this would update actual supply tracking system
    return { success: true, supplies };
  },

  // ============================================================================
  // REAL-TIME SUBSCRIPTIONS
  // ============================================================================

  /**
   * Subscribe to print queue updates in real-time
   * Monitors INSERT, UPDATE, DELETE events on print_jobs table
   */
  subscribeToPrintQueue(callback) {
    const channel = supabase
      ?.channel('print_queue_channel')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'print_jobs'
        },
        async (payload) => {
          // Fetch related employee and card data
          const { data: job } = await supabase
            ?.from('print_jobs')
            ?.select(`
              *,
              card:id_cards(
                *,
                employee:employees(*)
              )
            `)
            ?.eq('id', payload?.new?.id || payload?.old?.id)
            ?.single();

          const formattedJob = job ? {
            id: job?.id,
            cardId: job?.card_id,
            employeeName: `${job?.card?.employee?.first_name} ${job?.card?.employee?.last_name}`,
            department: job?.card?.employee?.department,
            status: job?.job_status,
            printerModel: job?.printer_model,
            batchId: job?.batch_id,
            priority: job?.priority,
            startedAt: job?.started_at,
            completedAt: job?.completed_at,
            errorMessage: job?.error_message,
            createdAt: job?.created_at,
            updatedAt: job?.updated_at
          } : null;

          callback(formattedJob, payload?.eventType);
        }
      )
      ?.subscribe();
    
    return channel;
  },

  /**
   * Subscribe to printer supply level updates in real-time
   * Monitors UPDATE events on printer_supplies table
   * Triggers alerts when supply levels reach reorder thresholds
   */
  subscribeToSupplyAlerts(callback) {
    const channel = supabase
      ?.channel('supply_alerts_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'printer_supplies'
        },
        (payload) => {
          const supply = {
            id: payload?.new?.id,
            supplyType: payload?.new?.supply_type,
            currentLevel: payload?.new?.current_level,
            unit: payload?.new?.unit,
            reorderThreshold: payload?.new?.reorder_threshold,
            lastRefilledAt: payload?.new?.last_refilled_at,
            estimatedDepletionDate: payload?.new?.estimated_depletion_date,
            metadata: payload?.new?.metadata,
            updatedAt: payload?.new?.updated_at
          };

          // Check if supply level is at or below reorder threshold
          const isLowSupply = supply?.currentLevel <= supply?.reorderThreshold;
          const isCritical = supply?.currentLevel <= (supply?.reorderThreshold * 0.5);

          callback(supply, {
            isLowSupply,
            isCritical,
            eventType: payload?.eventType
          });
        }
      )
      ?.subscribe();
    
    return channel;
  },

  /**
   * Subscribe to printer connectivity status updates in real-time
   * Monitors UPDATE events on printer_status table
   * Tracks connection status, errors, and maintenance schedules
   */
  subscribeToConnectivityStatus(callback) {
    const channel = supabase
      ?.channel('printer_connectivity_channel')
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'printer_status'
        },
        (payload) => {
          const status = {
            id: payload?.new?.id,
            printerModel: payload?.new?.printer_model,
            connectionStatus: payload?.new?.connection_status,
            isReady: payload?.new?.is_ready,
            totalPrintsLifetime: payload?.new?.total_prints_lifetime,
            errorCount: payload?.new?.error_count,
            lastMaintenanceDate: payload?.new?.last_maintenance_date,
            nextMaintenanceDate: payload?.new?.next_maintenance_date,
            firmwareVersion: payload?.new?.firmware_version,
            metadata: payload?.new?.metadata,
            updatedAt: payload?.new?.updated_at
          };

          // Determine if status change requires immediate attention
          const previousStatus = payload?.old?.connection_status;
          const isStatusChange = previousStatus !== status?.connectionStatus;
          const isDisconnected = status?.connectionStatus === 'disconnected';
          const hasError = status?.connectionStatus === 'error' || status?.errorCount > 0;

          callback(status, {
            isStatusChange,
            isDisconnected,
            hasError,
            previousStatus,
            eventType: payload?.eventType
          });
        }
      )
      ?.subscribe();
    
    return channel;
  },

  /**
   * Unsubscribe from a real-time channel
   * @param {Object} channel - The channel object returned from subscribe methods
   */
  async unsubscribe(channel) {
    if (channel) {
      await supabase?.removeChannel(channel);
    }
  },

  /**
   * Unsubscribe from all active channels
   */
  async unsubscribeAll() {
    await supabase?.removeAllChannels();
  }
};
